import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import { useBadgeCounts } from '../../contexts/BadgeCountsContext';
import EliteInvites from './EliteInvites';

// Elite access (2026-09-18): the review queue for verified members who asked for
// Elite, like Promoter/DJ requests. Katherine approves every request by hand.
const TABS = [
  { key: 'pending', label: 'Requests' },
  { key: 'approved', label: 'Members' },
  { key: 'declined', label: 'Declined' },
  { key: 'removed', label: 'Removed' },
  { key: 'invites', label: 'Invites' },
];

const btn = { borderRadius: 10, padding: '9px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const card = { display: 'flex', gap: 16, alignItems: 'center', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 16, flexWrap: 'wrap' };
const thumb = { width: 72, height: 72, borderRadius: 12, objectFit: 'cover', background: 'rgba(255,255,255,0.05)', cursor: 'zoom-in' };

const Elite = () => {
  const navigate = useNavigate();
  const { refreshCounts } = useBadgeCounts();
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [error, setError] = useState('');
  const tabRef = useRef(tab);
  const latestLoad = useRef(0);

  // Only the newest load for the tab on screen may fill the list. `silent`
  // refreshes after a decision without blanking the list.
  const load = async (status, { silent = false } = {}) => {
    const n = ++latestLoad.current;
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/elite/admin/requests', { params: { status } });
      if (n !== latestLoad.current || status !== tabRef.current) return;
      setRows(Array.isArray(res.data?.requests) ? res.data.requests : []);
      setCounts(res.data?.counts || {});
      setError('');
    } catch (err) {
      if (n !== latestLoad.current) return;
      console.log(`error loading Elite requests: ${err}`);
      setRows([]);
      setError(err?.response?.data?.message || 'Could not load Elite requests. Refresh to try again.');
    } finally {
      if (n === latestLoad.current) setLoading(false);
    }
  };

  useEffect(() => { tabRef.current = tab; if (tab !== 'invites') load(tab); }, [tab]);

  const act = async (r, run) => {
    setBusyId(r._id);
    try {
      await run();
      setRows((prev) => prev.filter((x) => x._id !== r._id));
      refreshCounts?.();
    } catch (e) {
      alert(e?.response?.data?.message || 'Could not update. Please try again.');
    } finally {
      setBusyId(null);
      load(tabRef.current, { silent: true });
    }
  };

  const decide = (r, decision) => {
    const who = r.user?.name || 'this person';
    const ask = decision === 'approve'
      ? `Approve ${who} for Elite? They get a push right away.`
      : `Decline ${who}'s Elite request? This can't be undone here.`;
    if (!window.confirm(ask)) return;
    act(r, () => api.post(`/elite/admin/requests/${r._id}/decision`, { decision }));
  };
  const remove = (r) => {
    if (!window.confirm(`Remove Elite from ${r.user?.name || 'this member'}?`)) return;
    act(r, () => api.post(`/elite/admin/users/${r.user?._id}/remove`));
  };

  const fmt = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return ''; }
  };

  const emptyText = {
    pending: "No one's waiting on Elite right now. Requests land here the moment a verified member taps Request Elite access in the app.",
    approved: 'No Elite members yet.',
    declined: 'No declined requests.',
    removed: 'No one has been removed from Elite.',
  }[tab];

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          {/* Claims its width, or .page-content lets it slide under the sidebar. */}
          <section style={{ padding: '20px 2vw', width: '100%', maxWidth: 1500, boxSizing: 'border-box' }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>Elite</h1>
            <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 13, margin: 0 }}>
              Verified members asking for Elite access (Elite-only nights). You approve every request.
            </p>

            <div style={{ display: 'flex', gap: 8, margin: '16px 0 22px' }}>
              {TABS.map(({ key, label }) => {
                const on = tab === key;
                const n = counts[key];
                return (
                  <button key={key} type="button" onClick={() => setTab(key)} style={{ ...btn, fontSize: 13, padding: '8px 16px', border: on ? 'none' : '1px solid rgba(255,255,255,.15)', background: on ? 'linear-gradient(135deg,#0A3FD6,#7229F5)' : 'transparent', color: on ? '#fff' : 'rgba(255,255,255,.6)' }}>
                    {label}{typeof n === 'number' ? ` · ${n}` : ''}
                  </button>
                );
              })}
            </div>

            {tab === 'invites' ? <EliteInvites onChanged={() => refreshCounts?.()} /> : (<>
            {loading && <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 13 }}>Loading…</p>}

            {!loading && error && (
              <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 14, padding: 20, color: '#f87171', marginBottom: 14 }}>
                {error}
              </div>
            )}

            {!loading && !error && rows.length === 0 && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 40, textAlign: 'center', color: 'rgba(255,255,255,.4)' }}>
                {emptyText}
              </div>
            )}

            {!loading && rows.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {rows.map((r) => {
                  const busy = busyId === r._id;
                  const atRequest = r.profile_photo_url || r.user?.photo_url;
                  const current = r.user?.photo_url;
                  const photoChanged = !!(current && r.profile_photo_url && current !== r.profile_photo_url);
                  const when = {
                    pending: `Requested ${fmt(r.created_at)}`,
                    approved: `Approved ${fmt(r.decided_at)}${r.decided_by ? ` by ${r.decided_by}` : ''}`,
                    declined: `Declined ${fmt(r.decided_at)}${r.decided_by ? ` by ${r.decided_by}` : ''}`,
                    removed: `Removed ${fmt(r.removed_at)}${r.removed_by ? ` by ${r.removed_by}` : ''}`,
                  }[r.status] || '';
                  return (
                    <div key={r._id} style={card}>
                      {/* the member's verification selfie next to their profile photo */}
                      <div style={{ display: 'flex', gap: 8 }}>
                        {r.selfie_url && <img onClick={() => setLightbox(r.selfie_url)} src={r.selfie_url} alt="selfie" title="Verification selfie" style={thumb} />}
                        <img onClick={() => atRequest && setLightbox(atRequest)} src={atRequest || ''} alt="profile" title="Profile photo at request time" style={thumb} />
                        {photoChanged && (
                          <img onClick={() => setLightbox(current)} src={current} alt="current profile" title="Current profile photo (changed since the request)" style={{ ...thumb, outline: '2px solid #f0a13c' }} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{r.user?.name || 'Unnamed'}</div>
                        {!r.user?.gone && r.user?.verified === false && (
                          <div style={{ color: '#f87171', fontSize: 12, fontWeight: 700, marginTop: 4 }}>Not verified. Elite comes after Get verified, so this can't be approved.</div>
                        )}
                        {!r.selfie_url && <div style={{ color: '#f0a13c', fontSize: 12, marginTop: 4 }}>No verification selfie on file. Compare with care.</div>}
                        <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
                          {r.instagram && <a href={`https://instagram.com/${r.instagram}`} target="_blank" rel="noreferrer" style={{ color: '#9d8bff', fontSize: 13, textDecoration: 'none' }}>@{r.instagram} · IG ↗</a>}
                          {r.tiktok && <a href={`https://tiktok.com/@${r.tiktok}`} target="_blank" rel="noreferrer" style={{ color: '#9d8bff', fontSize: 13, textDecoration: 'none' }}>@{r.tiktok} · TikTok ↗</a>}
                        </div>
                        {photoChanged && <div style={{ color: '#f0a13c', fontSize: 12, marginTop: 4 }}>Profile photo changed since the request (orange frame).</div>}
                        <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 11, marginTop: 6 }}>{when}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {!r.user?.gone && (
                          <button type="button" onClick={() => navigate(`/users/edit/${r.user?.id || r.user?._id}`)} style={{ ...btn, fontWeight: 600, background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.7)' }}>View profile</button>
                        )}
                        {r.status === 'pending' && (
                          <>
                            <button type="button" disabled={busy} onClick={() => decide(r, 'decline')} style={{ ...btn, background: 'transparent', border: '1px solid rgba(248,113,113,.4)', color: '#f87171' }}>Decline</button>
                            {!r.user?.gone && <button type="button" disabled={busy} onClick={() => decide(r, 'approve')} style={{ ...btn, padding: '9px 16px', border: 'none', background: 'linear-gradient(135deg,#0A3FD6,#7229F5)', color: '#fff' }}>{busy ? '…' : '✓ Approve'}</button>}
                          </>
                        )}
                        {r.status === 'approved' && r.user?._id && (
                          <button type="button" disabled={busy} onClick={() => remove(r)} style={{ ...btn, background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.6)' }}>{busy ? '…' : 'Remove Elite'}</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </>)}
          </section>
        </article>
      </div>
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'zoom-out' }}>
          <img src={lightbox} alt="full" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12 }} />
        </div>
      )}
    </main>
  );
};

export default Elite;
